import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./ContactModal.css";

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: null, message: "" });
  const [touched, setTouched] = useState({});

  const validateField = (name, value) => {
    let error = "";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const nameRegex = /^[a-zA-Z\s]{2,50}$/;

    switch (name) {
      case "name":
        if (!value.trim()) {
          error = "Name is required";
        } else if (!nameRegex.test(value)) {
          error =
            "Name should only contain letters and spaces (2-50 characters)";
        }
        break;
      case "email":
        if (!value.trim()) {
          error = "Email is required";
        } else if (!emailRegex.test(value)) {
          error = "Please enter a valid email address";
        }
        break;
      case "subject":
        if (!value.trim()) {
          error = "Subject is required";
        } else if (value.length < 3) {
          error = "Subject must be at least 3 characters long";
        } else if (value.length > 100) {
          error = "Subject must not exceed 100 characters";
        }
        break;
      case "message":
        if (!value.trim()) {
          error = "Message is required";
        } else if (value.length < 10) {
          error = "Message must be at least 10 characters long";
        } else if (value.length > 1000) {
          error = "Message must not exceed 1000 characters";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const validateForm = () => {
    const errors = {};
    Object.keys(formData).forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate field on change if it has been touched
    if (touched[name]) {
      const error = validateField(name, value);
      setFormErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }

    if (submitStatus.type) {
      setSubmitStatus({ type: null, message: "" });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name, value);
    setFormErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      name: true,
      email: true,
      subject: true,
      message: true,
    });

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: "" });

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      setSubmitStatus({
        type: "success",
        message: "Message sent successfully! I will get back to you soon.",
      });
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
      setTouched({});
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus({
        type: "error",
        message:
          error.message || "Failed to send message. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="contact-box" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close-button" onClick={onClose}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h2>Get in Touch</h2>
        <p className="contact-lead">Let&apos;s discuss your project</p>
        <p>
          Fill out the form below and I&apos;ll get back to you as soon as
          possible.
        </p>
        {submitStatus.type && (
          <div className={`submit-status ${submitStatus.type}`}>
            {submitStatus.message}
          </div>
        )}
        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-item">
            <input
              id="contact-name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={formErrors.name ? "error" : ""}
              placeholder=" "
              required
              maxLength={50}
            />
            <label htmlFor="contact-name">Name</label>
            {touched.name && formErrors.name && (
              <span className="error-message">{formErrors.name}</span>
            )}
          </div>
          <div className="form-item">
            <input
              id="contact-email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={formErrors.email ? "error" : ""}
              placeholder=" "
              required
            />
            <label htmlFor="contact-email">Email</label>
            {touched.email && formErrors.email && (
              <span className="error-message">{formErrors.email}</span>
            )}
          </div>
          <div className="form-item">
            <input
              id="contact-subject"
              name="subject"
              type="text"
              value={formData.subject}
              onChange={handleChange}
              onBlur={handleBlur}
              className={formErrors.subject ? "error" : ""}
              placeholder=" "
              required
              maxLength={100}
            />
            <label htmlFor="contact-subject">Subject</label>
            {touched.subject && formErrors.subject && (
              <span className="error-message">{formErrors.subject}</span>
            )}
          </div>
          <div className="form-item">
            <textarea
              id="contact-message"
              name="message"
              rows="4"
              value={formData.message}
              onChange={handleChange}
              onBlur={handleBlur}
              className={formErrors.message ? "error" : ""}
              placeholder=" "
              required
              maxLength={1000}
            />
            <label htmlFor="contact-message">Message</label>
            {touched.message && formErrors.message && (
              <span className="error-message">{formErrors.message}</span>
            )}
          </div>
          <button className="form-button" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
};

ContactModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ContactModal;
